/**
 * Typography tokens.
 *
 * Primary: AtlasInter (self-hosted approved prototype font)
 * Data/numeric: JetBrains Mono (self-hosted via @fontsource/jetbrains-mono)
 *
 * NEVER reference CDN fonts or system fallbacks in production. The fallbacks
 * in `fontFamily` are only for the brief window before the self-hosted file loads.
 */

export const fontFamily = {
  sans: [
    'AtlasInter',
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'system-ui',
    'sans-serif',
  ].join(', '),
  mono: ['JetBrains Mono', 'SF Mono', 'Menlo', 'Consolas', 'monospace'].join(', '),
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const fontSize = {
  /** Primary value on a dense operational surface. See tailwind-preset for why this step exists. */
  data: { size: '13px', lineHeight: '18px' },
  /** Supporting text beside a `data` value — counts, timestamps, chips. Plain, unlike `badge`. */
  micro: { size: '11px', lineHeight: '15px' },
  caption: { size: '12px', lineHeight: '16px' },
  label: { size: '12px', lineHeight: '16px', fontWeight: fontWeight.medium },
  body: { size: '14px', lineHeight: '20px' },
  'body-lg': { size: '16px', lineHeight: '24px' },
  h3: { size: '16px', lineHeight: '20px', fontWeight: fontWeight.semibold },
  subline: {
    size: '16px',
    lineHeight: '20px',
    fontWeight: fontWeight.bold,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
  },
  h2: { size: '20px', lineHeight: '24px', fontWeight: fontWeight.semibold },
  h1: { size: '28px', lineHeight: '32px', fontWeight: fontWeight.bold },
  display: { size: '32px', lineHeight: '36px', fontWeight: fontWeight.bold },
  badge: {
    size: '11px',
    lineHeight: '14px',
    fontWeight: fontWeight.semibold,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
  },
} as const;

export type FontWeight = keyof typeof fontWeight;
export type FontSizeToken = keyof typeof fontSize;
